import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CalendarDays, Home, MapPin } from "lucide-react";
import { Input, Badge, brandFillClass } from "@/components/ui";
import { ModifiedSectionCard } from "@/components/common";
import { formatDate } from "@/lib/datetime";
import type { House } from "@/types";

const META_CHIP_CLASS =
  "inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md bg-surface-100 px-2 py-0.5 text-xs font-medium text-surface-600";

interface HouseHeaderProps {
  house: House;
  nameInputProps: UseFormRegisterReturn<"name">;
  nameError: string | undefined;
  nameModified: boolean;
  /** Live listing state (admin form watches the unsaved toggle). */
  listed?: boolean;
  /** Top-right slot (e.g. the owning partner chip). */
  action?: ReactNode;
  /** Full-width strip at the bottom of the card (KPI metrics). */
  metrics?: ReactNode;
}

/** Hero card: editable name, listing status, location, owner, and the KPI strip. */
export function HouseHeader({
  house,
  nameInputProps,
  nameError,
  nameModified,
  listed = house.is_active,
  action,
  metrics,
}: HouseHeaderProps) {
  const { t } = useTranslation("houses");

  return (
    <ModifiedSectionCard
      modified={nameModified}
      className="mb-6 animate-slide-up overflow-hidden p-0"
    >
      <div className="relative px-5 py-5 sm:px-6">
        <div
          className="pointer-events-none absolute -top-24 -left-16 h-56 w-72 rounded-full bg-primary-400/15 blur-3xl dark:bg-primary-500/10"
          aria-hidden
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-glow ring-1 ring-inset ring-white/15 sm:h-14 sm:w-14", brandFillClass("primary"))}>
              <Home className="h-6 w-6 sm:h-7 sm:w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <Input
                {...nameInputProps}
                error={nameError}
                aria-label={t("adminDetail.houseName")}
                className="-mx-1.5 h-auto w-[calc(100%+0.75rem)] rounded-md border-transparent bg-transparent px-1.5 py-0.5 text-2xl font-semibold tracking-tight text-surface-900 shadow-none hover:border-surface-200 hover:bg-surface-50 sm:text-[26px] dark:bg-transparent dark:hover:bg-surface-100/60"
                placeholder={t("adminDetail.placeholderHouseName")}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant={listed ? "success" : "default"} size="sm" dot>
                  {listed ? t("list.active") : t("list.inactive")}
                </Badge>
                {house.address ? (
                  <span className={META_CHIP_CLASS} title={house.address}>
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
                    <span className="truncate">{house.address}</span>
                  </span>
                ) : null}
                <span className={META_CHIP_CLASS}>
                  <CalendarDays className="h-3.5 w-3.5 shrink-0 text-surface-400" aria-hidden />
                  <span className="tabular-nums">
                    {t("detail.createdOn", { date: formatDate(house.created_at) })}
                  </span>
                </span>
              </div>
            </div>
          </div>
          {action ? (
            <div className={nameModified ? "shrink-0 sm:pt-8" : "shrink-0"}>{action}</div>
          ) : null}
        </div>
      </div>
      {metrics}
    </ModifiedSectionCard>
  );
}
